import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import {
  createTheme,
  ThemeProvider as MaterialThemeProvider,
} from "@mui/material";
import styled from "@emotion/styled";
import { ConfigProvider, theme as antdtheme } from "antd";
import { green } from "@mui/material/colors";
import { CustomThemeType } from "./Providers";
import { Global, css } from "@emotion/react";

// --- Theme Variants (copy from Providers.tsx or import if exported) ---
export const themeVariants: {
  name: string;
  mode: "light" | "dark";
  theme: CustomThemeType;
}[] = [
  {
    name: "Light — Warm",
    mode: "light",
    theme: {
      brand: "#927a53",
      accent: "#7d3400",
      error: "#e45e5e",
      brandContrast: "#FFFFFF",
      accentContrast: "#000000",
      foreground: "#595553",
      textSecondary: "#868079", // lighter secondary text
      background: "#f6eee2",
      mainBackground: "#f6f2ec",
      ctaColor: "#a34a34",
      ctaContrast: "#ffffff",
      hoverColor: "#f3e7d5",
      dividerColor: "#dbd0bf",
    },
  },
  {
    name: "Dark — Warm",
    mode: "dark",
    theme: {
      brand: "#d8b47a", // lighter, glowing version of your brand
      accent: "#ff914d", // warm accent that pops against dark
      error: "#df7661",
      brandContrast: "#000000", // inverted from light theme
      accentContrast: "#ffffff", // ensures clarity
      foreground: "#d9cfc0", // soft warm gray for text
      textSecondary: "#a39d94", // lighter secondary text
      mainBackground: "#1e1a16", // deep warm brown/charcoal
      background: "#26211c", // slightly lighter for contrast layering
      ctaColor: "#ff6b4a", // warm call-to-action that stands out
      ctaContrast: "#000000", // ensures readability
      hoverColor: "#3a322b", // subtle warm hover shade
      dividerColor: "#4a4137", // muted divider that blends with dark bg
    },
  },
];

// --- Context for theme index and setter ---
interface ThemesContextType {
  themeIndex: number;
  setThemeIndex: (idx: number, persist?: boolean) => void;
  themeVariant: (typeof themeVariants)[number];
}
const ThemesContext = createContext<ThemesContextType>({
  themeIndex: 0,
  setThemeIndex: () => {},
  themeVariant: themeVariants[0],
});

export const useThemes = () => useContext(ThemesContext);

// --- AppWrapper styled component ---
const AppWrapper = styled.div<{ theme?: CustomThemeType }>`
  background-color: ${(p) => p.theme.background};
  & .icon-inner svg {
    stroke: ${(p) => p.theme.brand} !important;
  }
  & .icon-inner {
    background-color: red;
  }
`;

// --- ThemesProvider component ---
export const ThemesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [themeIndex, setThemeIndexState] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem("cl-theme-index");
      const parsed = stored !== null ? Number.parseInt(stored, 10) : NaN;
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < themeVariants.length) {
        return parsed;
      }
    }
    return 0;
  });

  const themeVariant = themeVariants[themeIndex] ?? themeVariants[0];
  const theme = themeVariant.theme;
  const paletteMode = themeVariant.mode;

  // Memoize MUI theme for performance
  const materialTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: paletteMode,
          primary: {
            main: theme.brand,
            dark: theme.brand,
          },
          secondary: {
            main: theme.accent,
          },
          success: {
            main: green[600],
            light: green[50],
            dark: green[800],
          },
          background: {
            paper: theme.background,
            default: theme.mainBackground,
          },
          action: {
            hover: theme.hoverColor,
            selected: (theme.hoverColor as any) + 20,
          },
          text: {
            primary: theme.foreground,
            secondary: theme.textSecondary,
          },
          divider: theme.dividerColor,
          error: {
            main: theme.error
          }
        },
        typography: {
          fontFamily: "Inter",
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                "&:hover": {
                  backgroundColor: theme.hoverColor,
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                color: theme.brand,
                "&.Mui-checked": {
                  color: theme.ctaColor,
                },
              },
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                fontSize: "1.5rem",
                color: theme.brand,
              },
            },
          },
        },
      }),
    [theme, paletteMode]
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("cl-theme-index", String(themeIndex));
      window.localStorage.setItem("cl-theme-mode", themeVariant.mode);
    }
  }, [themeIndex, themeVariant.mode]);

  const setThemeIndex = (idx: number) => {
    setThemeIndexState(idx);
  };

  return (
    <ThemesContext.Provider value={{ themeIndex, setThemeIndex, themeVariant }}>
      <EmotionThemeProvider theme={theme}>
        {/* Global scrollbar styles */}
        <Global
          styles={css`
            /* For Chrome, Edge, and Safari */
            ::-webkit-scrollbar {
              width: 12px;
              background: ${theme.mainBackground};
            }
            ::-webkit-scrollbar-thumb {
              background: ${theme.background};
              border-radius: 8px;
            }
            ::-webkit-scrollbar-thumb:hover {
              background: ${theme.hoverColor};
            }
            /* For Firefox */
            * {
              scrollbar-width: thin;
              scrollbar-color: color-mix(in hsl, ${theme.background}, ${theme.foreground} 5%) ${theme.mainBackground};
            }
          `}
        />
        <MaterialThemeProvider theme={materialTheme}>
          <AppWrapper>
            <ConfigProvider
              theme={{
                algorithm: antdtheme.defaultAlgorithm,
                components: {
                  Collapse: {
                    contentPadding: 0,
                    headerPadding: "1rem 1rem 1rem 1rem",
                    headerBg: "transparent",
                  },
                },
              }}
            >
              {children}
            </ConfigProvider>
          </AppWrapper>
        </MaterialThemeProvider>
      </EmotionThemeProvider>
    </ThemesContext.Provider>
  );
};
